<template>
  <section>
    <div class="d-flex justify-content-between mb-3">
      <div>
        <h2>People</h2>
        <span v-if="capability" class="tag">{{ capability }}</span>
      </div>
      <div class="d-flex justify-content-between align-items-center gap-1">
        <div class="d-md-flex d-none search-input-container inline-title">
          <IconMagnifyingGlass />
          <input
            v-model="searchValue"
            :class="{
              'form-control': true,
              'filters-search-input': !modalPeopleStore.openAsModal
            }"
            type="text"
            placeholder="Search for name, title..."
            @input="debouncedSearch"
          />
        </div>
        <div>
          <button class="btn" style="padding: 10px">
            <IconShare @click="copyUrlToClipboard()" />
          </button>
        </div>
        <slot name="close" />
      </div>
    </div>

    <div class="d-flex align-items-center modal-filters">
      <div class="flex-grow-1">
        <div class="row filters-gap-x row-gap-2">
          <div class="d-flex d-md-none col-lg-3">
            <div class="d-flex justify-content-between w-100">
              <div class="search-input-container w-100">
                <IconMagnifyingGlass />
                <input
                  v-model="searchValue"
                  class="form-control"
                  type="text"
                  placeholder="Search for name, title..."
                  @input="debouncedSearch"
                />
              </div>
            </div>
          </div>
          <div
            v-if="
              modalPeopleStore.capabilities.length > 0 &&
              usePageStore().page?.system.type != contentTypes.capabilities_page.codename
            "
            class="col-md-6 col-lg-3"
          >
            <div class="dropdown">
              <div
                id="dropdownMenuButtonCapability"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{
                  (selectedCapability && typeof selectedCapability === 'string'
                    ? selectedCapability.slice(0, 20)
                    : 'Capability') +
                  (selectedCapability && selectedCapability.length > 20 ? '...' : '')
                }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonCapability">
                <li>
                  <a class="dropdown-item" @click="changeCapability('')"> Capability </a>
                </li>
                <li
                  v-for="selectedCapability in modalPeopleStore.capabilities"
                  :key="selectedCapability"
                >
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="changeCapability(selectedCapability)"
                  >
                    {{ selectedCapability }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="dropdown">
              <div
                id="dropdownMenuButtonLocation"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ modalPeopleStore.getSelectedLocationText() || 'Location' }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonLocation">
                <li key="Location">
                  <a class="dropdown-item" @click="changeLocation('')"> Location </a>
                </li>
                <li v-for="location in modalPeopleStore.locations" :key="location.value">
                  <a class="dropdown-item" href="#" @click.prevent="changeLocation(location.value)">
                    {{ location.name }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="dropdown">
              <div
                id="dropdownMenuButtonPosition"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ selectedPosition || 'Position' }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonPosition">
                <li key="Position">
                  <a class="dropdown-item" @click="changePosition('')"> Position </a>
                </li>
                <li v-for="position in modalPeopleStore.positions" :key="position">
                  <a class="dropdown-item" href="#" @click.prevent="changePosition(position)">
                    {{ position }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="dropdown">
              <div
                id="dropdownMenuButtonLawSchool"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ selectedLawSchool || 'School' }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonLawSchool">
                <li key="School">
                  <a class="dropdown-item" @click="changeLawSchool('')"> School </a>
                </li>
                <li v-for="school in modalPeopleStore.lawSchools" :key="school">
                  <a class="dropdown-item" href="#" @click.prevent="changeLawSchool(school)">
                    {{ school }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-shrink-0">
        <button
          class="d-none d-lg-inline-block btn btn-light clear-all flex-grow-1 text-nowrap align-self-center"
          style="padding-right: 2px"
          @click="clearFilters()"
        >
          Clear All
        </button>
      </div>
    </div>

    <div class="d-flex justify-content-between mb-2 align-items-center" style="height: 44px">
      <div
        v-if="modalPeopleStore.items.length > 0"
        class="d-none d-lg-inline-block showing-results"
      >
        Showing 1 -
        {{
          modalPeopleStore.size <= modalPeopleStore.totalResults
            ? modalPeopleStore.size
            : modalPeopleStore.totalResults
        }}
        of {{ modalPeopleStore.totalResults }} results
      </div>
      <div v-else> 0 results </div>
      <button
        class="d-lg-none d-inline-block btn btn-light clear-all flex-grow-1 text-nowrap text-start ps-0"
        style="padding-right: 2px"
        @click="clearFilters()"
      >
        Clear All
      </button>
      <div class="d-flex align-items-center">
        <span class="d-none d-sm-inline-block sort-dropdown-title">Sort by</span>
        <select
          v-model="sortBy"
          class="btn btn-light sort-dropdown px-0 pe-2"
          @change="modalPeopleStore.setSortBy(sortBy)"
        >
          <option :value="modalPeopleStore.sortOrders.nameAsc">A to Z</option>
          <option :value="modalPeopleStore.sortOrders.nameDesc">Z to A</option>
        </select>
        <div class="sort-buttons">
          <button
            v-element-hover="onGridBtnHovered"
            :class="{ 'sort-view sort-grid': true, active: isGrid }"
            @click="modalPeopleStore.setIsGrid(true)"
          >
            <IconSquaresFour v-if="isGrid || isGridBtnHovered" />
            <IconSquaresFourInactive v-else />
          </button>
          <button
            v-element-hover="onListBtnHovered"
            :class="{ 'sort-view sort-list': true, active: !isGrid }"
            @click="modalPeopleStore.setIsGrid(false)"
          >
            <IconRowsInactive v-if="!isGrid" />
            <template v-else>
              <IconRows v-if="!isListBtnHovered" />
              <IconRowsHovered v-else />
            </template>
          </button>
        </div>
      </div>
    </div>

    <div class="d-lg-none d-inline-block showing-results"
      >Showing {{ modalPeopleStore.items.length }} results</div
    >
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import debounce from 'lodash/debounce'
import { usePeopleModalStore } from '~/stores/modals/people-modal'
import { vElementHover } from '@vueuse/components'
import { useToast } from 'vue-toast-notification'
import { contentTypes } from '~/models'

const pageStore = usePageStore()
const modalPeopleStore = usePeopleModalStore()
const { isGrid, selectedCapability, selectedPosition, selectedLawSchool, sortBy, searchValue } =
  storeToRefs(modalPeopleStore)
const capability = pageStore.page?.elements.capability?.value[0]?.name || ''

const debouncedSearch = debounce(() => {
  modalPeopleStore.setSearchValue(searchValue.value)
  usePaginationStore().changePage(1)
}, 1000)

function changePosition(position: string) {
  modalPeopleStore.setSelectedPosition(position)
}
function changeCapability(capability: string) {
  modalPeopleStore.setSelectedCapability(capability)
}
function changeLocation(location: string) {
  modalPeopleStore.setSelectedLocation(location)
}
function changeLawSchool(lawSchool: string) {
  modalPeopleStore.setSelectedLawSchool(lawSchool)
}
function clearFilters() {
  modalPeopleStore.clearFilters()
}
async function copyUrlToClipboard() {
  const queryParams = {
    l: modalPeopleStore.selectedLocation || '',
    c: modalPeopleStore.selectedCapability || '',
    p: modalPeopleStore.selectedPosition || '',
    s: modalPeopleStore.selectedLawSchool || '',
    q: modalPeopleStore.searchValue || ''
  }
  const url = new URL(peopleUrlList, window.location.origin)
  Object.keys(queryParams).forEach((key) => {
    const queryKey = key as keyof typeof queryParams
    if (queryParams[queryKey]) {
      url.searchParams.append(queryKey, queryParams[queryKey])
    }
  })
  const toast = useToast()
  try {
    await navigator.clipboard.writeText(url.toString())

    toast.success('URL copied to clipboard.', {
      position: 'bottom-right'
    })
  } catch (error) {
    console.error('Failed to copy the URL: ', error)
    toast.error('Failed to copy the URL.', {
      position: 'bottom-right'
    })
  }
}

const isGridBtnHovered = ref(false)
function onGridBtnHovered(state: boolean) {
  isGridBtnHovered.value = state
}
const isListBtnHovered = ref(false)
function onListBtnHovered(state: boolean) {
  isListBtnHovered.value = state
}
</script>
